import { cellClickingTelemetryContext, DatabasesDisplayGrid_columns, databasesWithCrassCellFormatters, TablesDisplayGrid_columns, tablesWithCrassCellFormatters } from '@tableau/data-catalog-client-components';
import { DataGrid, GridSelection } from '@tableau/widgets-datagrid';
import * as React from 'react';
import * as _ from 'underscore';

import { areFlowParametersEnabled } from '~src/flow/utils/FlowParametersUtils';
import { areLinkedTasksEnabled } from '~src/linkedTasks/utils/LinkedTaskHelpers';

import { TelemetryEvents } from '../../../common/telemetryEvents/TelemetryEvents';
import { FlowConnectionErrorColumn } from '../../../flow/components/dataGridColumns/FlowConnectionErrorColumn';
import { FlowConnectionInputStepsColumn } from '../../../flow/components/dataGridColumns/FlowConnectionInputStepsColumn';
import { FlowConnectionOutputStepsColumn } from '../../../flow/components/dataGridColumns/FlowConnectionOutputStepsColumn';
import { searchTelemetry } from '../../../search/components/SearchTelemetry';
import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';
import { ServerSettingsUnauthenticated } from '../../../settings/services/ServerSettingsUnauthenticated';
import { SiteAuthenticationSettingsService } from '../../../settings/siteAuthSettings/services/SiteAuthenticationSettingsService';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { ExplorableItemType, getKeyForExplorableItem } from '../../../ts/models/ContentType';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IExplorableItemTypeInfo, IFavoritableItemInfo } from '../../../ts/models/resourceInfoParts';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { SearchTermDataContext } from '../../contexts/TelemetryContexts';
import { ItemsSet } from '../../ItemsSet';
import { DataRoleSubspaceName, DatasourceSubspaceName, FlowSubspaceName, GroupSubspaceName, isAllServerUsersSpace, isAllSitesSpace, isBackgroundJobsSpace, isCatalogDatabaseWithCrassSpace, isCatalogSearchSpace, isCatalogSpace, isCatalogTableWithCrassSpace, isCollectionsSpace, isDatabasesSearchSpace, isDatabasesWithCrassSearchSpace, isDataRoleSpace, isDatasourceSpace, isFavoritesSpace, isFlowSpace, isGroupSpace, isGroupUsersSubspace, isMyCollectionsSubspace, isPersonalSpace, isProjectSpace, isPublishedConnectionSpace, isPublishedConnectionTableSpace, isRecentsSpace, isScheduleSpace, isServerTasksSpace, isSharedWithMeSpace, isSingleCollectionSpace, isSiteTasksSpace, isSiteUserCollectionsSubspace, isSiteUserContentSubspace, isSiteUserDataAlertsSubSpace, isSiteUserSpace, isSiteUsersSpace, isTablesWithCrassSearchSpace, isTablesWithSearchSpace, isUnifiedSearchSpace, isWorkbookSpace, PublishedConnectionSubspaceName, PublishedConnectionTableSubspaceName, ScheduleSubspaceName, ServerTasksSubspaceName, SiteTasksSubspaceName, SpaceInfo, WorkbookSubspaceName } from '../../state/SpaceInfo';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ColumnType, ListViewGridColumnGenerator } from './ListViewGridColumnGenerator';
import { CommonColumn } from './types/Columns';
import { replaceColumnCellFormattersForDatabasesAndTables } from './utils/ListViewGridContentGeneratorUtils';

export interface ListViewGridContentGeneratorBaseParams {
  readonly items: ReadonlyArray<IExplorableItemTypeInfo>;
  readonly selectedItems: ItemsSet<IExplorableItemTypeInfo>;
  readonly selectItem: (item: IContentInfo) => void;
  readonly deselectItem: (item: IContentInfo) => void;
  readonly requestSpaceContentsMissingSlice: (start: number, end: number) => void;
  readonly spaceInfo: SpaceInfo;
  readonly contentFilter: ReadonlyArray<ExplorableItemType>;
  readonly currentSort: ReadonlyArray<IOrderClause>;
  readonly changeSort: (field: string) => void;
  readonly setSortOrder: (order: ReadonlyArray<IOrderClause>) => void;
  readonly isSessionSiteOrServerAdmin: boolean;
  readonly displaySchedulesInClientTimezone: boolean;
  readonly ignoreExtractTaskPriority: boolean;
  readonly displayScheduleDescriptionAsName: boolean;
  readonly inServerArea: boolean;
  readonly flowFailureThresholdForRunPrevention: number;
  readonly isGuestEnabled: boolean;
  readonly urls: SpacesSiteUrls;
}

export interface FavoriteActionsParams {
  readonly addContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => any;
}

export type ListViewGridContentGeneratorParams = ListViewGridContentGeneratorBaseParams & FavoriteActionsParams;

export const ColumnsForContentType: Record<ExplorableItemType, ColumnType[]> = {
  'accelerationTask': [],
  'backgroundJob': [CommonColumn.Select, CommonColumn.Id, CommonColumn.ActionsMenu, CommonColumn.Status, CommonColumn.BackgroundJobPriority, CommonColumn.BackgroundJobType, CommonColumn.JobRequestedTime, CommonColumn.CurrentRunTime, CommonColumn.CurrentQueueTime, CommonColumn.AverageRunTime, CommonColumn.AverageQueueTime],
  'collection': [CommonColumn.Select, CommonColumn.Favorite, /*CommonColumn.ContentType,*/ CommonColumn.CollectionName, CommonColumn.ActionsMenu, CommonColumn.Owner, /*CommonColumn.CollectionVisibility,*/ CommonColumn.CollectionItemCount, CommonColumn.CreatedTime, CommonColumn.UpdatedTime],
  'connectedApplication': [CommonColumn.Select, CommonColumn.Name, CommonColumn.ConnectedApplicationClientId, CommonColumn.ConnectedApplicationStatus, CommonColumn.ActionsMenu, CommonColumn.Created, CommonColumn.ConnectedApplicationScope, CommonColumn.ConnectedApplicationDomains],
  'connectedWorkbook': [],
  'connection': [],
  'customView': [CommonColumn.Select, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.OriginalViewName, CommonColumn.OwnerWithIcon, CommonColumn.Hits, CommonColumn.LastAccessedAt, CommonColumn.Modified],
  'dataalert': [CommonColumn.Select, CommonColumn.DataAlertSubject, CommonColumn.ActionsMenu, CommonColumn.Owner, CommonColumn.DataAlertView, CommonColumn.DataAlertRecepient, CommonColumn.DataAlertVisibleTo, CommonColumn.DataAlertFrequency, CommonColumn.DataAlertLastCheck, CommonColumn.DataAlertLast],
  'database': [CommonColumn.Select, ...DatabasesDisplayGrid_columns],
  'databaseWithCrass': [CommonColumn.Select, CommonColumn.TypeForCrass, CommonColumn.Certification, CommonColumn.DataQualityWarning, CommonColumn.Name, CommonColumn.DownstreamWorkbookCount, CommonColumn.DownstreamDataSourcesCount, CommonColumn.ServerOrPath, CommonColumn.ConnectionTypeInCrass, CommonColumn.Contact],
  'dataconnection': [CommonColumn.Select, CommonColumn.ConnectsTo, CommonColumn.ActionsMenu, CommonColumn.ConnectionType, CommonColumn.Authentication, CommonColumn.Username, CommonColumn.Datasource],
  'dataRole': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.Project, CommonColumn.Location, CommonColumn.Owner, CommonColumn.Modified],
  'datasource': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Certification, CommonColumn.DataQualityIndicator, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.Hits, CommonColumn.DatasourceInWorkbooksCount, CommonColumn.ConnectsTo, CommonColumn.Project, CommonColumn.Location, CommonColumn.Owner, CommonColumn.ExtractStatus, CommonColumn.ModifiedBy, CommonColumn.Alert],
  'extractTask': [],
  'flow': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.DataQualityIndicator, CommonColumn.ContentType, CommonColumn.NameWithMarkers, CommonColumn.ActionsMenu, CommonColumn.Project, CommonColumn.Location, CommonColumn.Owner, CommonColumn.Modified],
  'flowOutputStepRun': [],
  'group': [CommonColumn.Select, CommonColumn.GroupName, CommonColumn.ActionsMenu, CommonColumn.GroupDomain, CommonColumn.GroupUserCount, CommonColumn.GroupMinSiteRole, CommonColumn.GroupLicenseOnSignIn],
  'lens': [CommonColumn.Select, ...FeatureFlags.askDataLensFavoritingEnabled ? [CommonColumn.Favorite] : [], CommonColumn.ContentType, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.Project, CommonColumn.Location, CommonColumn.Owner, CommonColumn.Modified],
  'metric': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.MetricValue, CommonColumn.Hits, /*CommonColumn.MetricConnectedView,*/ CommonColumn.Project, CommonColumn.Location, CommonColumn.Owner, CommonColumn.MetricUpdated, CommonColumn.Modified],
  'project': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.ProjectsCount, CommonColumn.WorkbooksCount, CommonColumn.ViewsCount, CommonColumn.DatasourcesCount, CommonColumn.Owner, CommonColumn.Created],
  'publishedConnection': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.Project, CommonColumn.Location, CommonColumn.Owner, CommonColumn.Modified],
  'publishedConnectionTable': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.Project, CommonColumn.Location, CommonColumn.Owner, CommonColumn.Modified],
  'publishedConnectionColumn': [],
  'recommendedView': [],
  'remoteRefreshSchedule': [],
  'resourceGovernanceSetting': [CommonColumn.Select, CommonColumn.ResourceGovernanceSite, CommonColumn.ActionsMenu, CommonColumn.ResourceGovernanceConfiguration, CommonColumn.ResourceGovernanceExtracts, CommonColumn.ResourceGovernanceSubscriptions, CommonColumn.ResourceGovernanceFlows, CommonColumn.ResourceGovernanceRuntimeLimits, CommonColumn.ResourceGovernanceActualRuntime, CommonColumn.ResourceGovernanceJobs],
  'runFlowTask': [],
  'schedule': [CommonColumn.Select, CommonColumn.ScheduleName, CommonColumn.ActionsMenu, CommonColumn.ScheduleFrequency, CommonColumn.ScheduleTaskType, CommonColumn.ScheduleTasks, CommonColumn.ScheduleLinkedTask, CommonColumn.ScheduleExecution, CommonColumn.ScheduleNextRunAt],
  'searchuser': [CommonColumn.SiteUserDisplayName, CommonColumn.SiteUserName, CommonColumn.SiteUserDomainName, CommonColumn.SearchUserOwnedViewsCount, CommonColumn.SearchUserOwnedWorkbooksCount, CommonColumn.SearchUserOwnedDatasourcesCount],
  'serveruser': [CommonColumn.Select, CommonColumn.ServerUserDisplayName, CommonColumn.ActionsMenu, CommonColumn.SiteUserName, CommonColumn.ServerUserMaxSiteRole, CommonColumn.ServerUserLicenseLevel, CommonColumn.ServerUserSiteCounts, CommonColumn.SiteUserDomainName, CommonColumn.SiteUserLastSignin],
  'userIdentityConflict': [CommonColumn.Select, CommonColumn.ServerUserDisplayName, CommonColumn.SiteUserName, CommonColumn.SiteUserDomainName, CommonColumn.ActionsMenu, CommonColumn.IdentityConflictFailureType],
  'site': [CommonColumn.Select, CommonColumn.SiteNameLink, CommonColumn.ActionsMenu, CommonColumn.SiteUserCount, CommonColumn.SiteAdminCount, CommonColumn.SiteUserQuota, CommonColumn.SiteStorageUsed, CommonColumn.SiteStorageQuota, CommonColumn.SiteStatus, CommonColumn.SiteMetricsEnabled, CommonColumn.SiteAuthoringEnabled, CommonColumn.SiteSheetImageEnabled, CommonColumn.SiteEditMembership, CommonColumn.SiteExtractEncryption, CommonColumn.SiteAskDataMode, CommonColumn.SiteGuestAccessEnabled, CommonColumn.Created],
  'siteuser': [CommonColumn.Select, CommonColumn.SiteUserDisplayName, CommonColumn.ActionsMenu, CommonColumn.SiteUserName, CommonColumn.SiteUserRole, CommonColumn.SiteUserGroupCount, CommonColumn.SiteUserDomainName, CommonColumn.SiteUserAuthentication, CommonColumn.SiteUserLastSignin],
  'subscription': [CommonColumn.Select, CommonColumn.SubscribedTo, CommonColumn.ActionsMenu, CommonColumn.Subject, CommonColumn.Schedule, CommonColumn.SubscribedBy, CommonColumn.LastUpdate, CommonColumn.RunNextAt],
  'table': [CommonColumn.Select, ...TablesDisplayGrid_columns],
  'tableWithCrass': [CommonColumn.Select, CommonColumn.TypeForCrass, CommonColumn.Certification, CommonColumn.DataQualityWarning, CommonColumn.Name, CommonColumn.DownstreamWorkbookCount, CommonColumn.DownstreamDataSourcesCount, CommonColumn.TableDatabase, CommonColumn.TableColumns, CommonColumn.Contact],
  'view': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.Hits, CommonColumn.Workbook, CommonColumn.SheetNumber, CommonColumn.Project, CommonColumn.Location, CommonColumn.Owner, CommonColumn.Modified],
  'workbook': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.NameWithMarkers, CommonColumn.ActionsMenu, CommonColumn.Hits, CommonColumn.SheetsCount, CommonColumn.Size, CommonColumn.Project, CommonColumn.Location, CommonColumn.Owner, CommonColumn.Modified, CommonColumn.ModifiedBy, CommonColumn.Alert],
  'workbookAcceleration': [],
};

export const ColumnsForMixedContentTypes: ColumnType[] = [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.NameWithMarkers, CommonColumn.ActionsMenu, CommonColumn.MetricValue, CommonColumn.Owner, CommonColumn.Modified];
export const ColumnsForMixedContentTypesWithoutMetrics: ColumnType[] = [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.NameWithMarkers, CommonColumn.ActionsMenu, CommonColumn.Owner, CommonColumn.Modified];

export const ColumnsForMixedContentTypesWithLocation = [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.NameWithMarkers, CommonColumn.ActionsMenu, CommonColumn.MetricValue, CommonColumn.Location, CommonColumn.Owner, CommonColumn.Modified];
export const ColumnsForMixedContentTypesWithoutMetricsWithLocation = [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.NameWithMarkers, CommonColumn.ActionsMenu, CommonColumn.Location, CommonColumn.Owner, CommonColumn.Modified];

export const ColumnsForCRASSSearch: ColumnType[] = [
  CommonColumn.Select,
  CommonColumn.ContentType,
  CommonColumn.Name,
  CommonColumn.SheetsCount,
  CommonColumn.Hits,
  CommonColumn.DatasourceInWorkbooksCount,
  CommonColumn.FlowFileType,
  CommonColumn.Owner,
  CommonColumn.Modified];

export const ColumnsForGroupSubspaces: Record<GroupSubspaceName, ColumnType[]> = {
  'details': [],
  'users': [CommonColumn.Select, CommonColumn.SiteUserDisplayName, CommonColumn.ActionsMenu, CommonColumn.SiteUserName, CommonColumn.SiteUserRole, CommonColumn.SiteUserGroupCount, CommonColumn.SiteUserDomainName, CommonColumn.SiteUserAuthentication, CommonColumn.SiteUserLastSignin],
};

export const ColumnsForWorkbookSubspaces: Record<WorkbookSubspaceName, ColumnType[]> = {
  'datasources': [CommonColumn.Select, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.ConnectsTo, CommonColumn.ExtractStatusOnly, CommonColumn.Alert],
  'dataconnections': [CommonColumn.Select, CommonColumn.ConnectsTo, CommonColumn.ActionsMenu, CommonColumn.ConnectionType, CommonColumn.Authentication, CommonColumn.Username, CommonColumn.Datasource],
  'views': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.Acceleration, CommonColumn.Hits, CommonColumn.SheetNumber],
  'customViews': [CommonColumn.Select, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.OriginalViewName, CommonColumn.OwnerWithIcon, CommonColumn.Hits, CommonColumn.LastAccessedAt, CommonColumn.Modified],
  'subscriptions': [CommonColumn.Select, CommonColumn.UsernameForSubscription, CommonColumn.ActionsMenu, CommonColumn.SubscribedTo, CommonColumn.Subject, CommonColumn.Schedule, CommonColumn.SubscribedBy, CommonColumn.LastUpdate, CommonColumn.RunNextAt],
  'extractTasks': [CommonColumn.Select, CommonColumn.TaskType, CommonColumn.ActionsMenu, CommonColumn.Schedule, CommonColumn.Priority, CommonColumn.LastRunAtExtractRefreshes, CommonColumn.RunNextAt],
  'connectedMetrics': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.MetricValue, CommonColumn.Hits, /*CommonColumn.MetricConnectedView,*/ CommonColumn.Project, CommonColumn.Location, CommonColumn.Owner, CommonColumn.Modified, CommonColumn.MetricUpdated],
  'lineage': [],
  'workbookAccelerations': [CommonColumn.ContentType, CommonColumn.Name, CommonColumn.IsAccelerated, CommonColumn.SpeedUp, CommonColumn.AvgNonAcceleratedPLT, CommonColumn.AvgAcceleratedPLT, CommonColumn.OptimizationNotes, CommonColumn.Hits, CommonColumn.SheetNumber],
};

export const ColumnsForDataRoleSubspaces: Record<DataRoleSubspaceName, ColumnType[]> = {
  'definition': [],
  'connectedFlows': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.Hits, CommonColumn.SheetsCount, CommonColumn.Size, CommonColumn.Project, CommonColumn.Location, CommonColumn.Owner, CommonColumn.Modified, CommonColumn.Alert],
  'connectedDatasources': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.Hits, CommonColumn.SheetsCount, CommonColumn.Size, CommonColumn.Project, CommonColumn.Location, CommonColumn.Owner, CommonColumn.Modified, CommonColumn.Alert],
};

export const ColumnsForDatasourceSubspaces: Record<DatasourceSubspaceName, ColumnType[]> = {
  'lineage': [],
  'nlp': [],
  'connections': [CommonColumn.Select, CommonColumn.ConnectsTo, CommonColumn.ActionsMenu, CommonColumn.ConnectionType, CommonColumn.Authentication, CommonColumn.Username],
  'extractRefreshes': [CommonColumn.Select, CommonColumn.TaskType, CommonColumn.ActionsMenu, CommonColumn.Schedule, CommonColumn.Priority, CommonColumn.LastRunAt, CommonColumn.RunNextAt],
  'remoteRefreshSchedules': [CommonColumn.Select, CommonColumn.RemoteRefreshType, CommonColumn.ActionsMenu, CommonColumn.Definition, CommonColumn.RemoteRefreshPriority, CommonColumn.LastRunAt, CommonColumn.RunNextAt],
  'connectedWorkbooks': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.Hits, CommonColumn.SheetsCount, CommonColumn.Size, CommonColumn.Project, CommonColumn.Location, CommonColumn.Owner, CommonColumn.Modified, CommonColumn.Alert],
  'askDataUsageAnalytics': [],
};

export const ColumnsForRecentsViewsSpace: ColumnType[] = [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.Workbook, CommonColumn.LastAccessedAt, CommonColumn.Project, CommonColumn.Location, CommonColumn.Owner, CommonColumn.Modified];
export const ColumnsForRecentsMixedSpace: ColumnType[] = [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.ActionsMenu, CommonColumn.MetricValue, CommonColumn.Workbook, CommonColumn.LastAccessedAt, CommonColumn.Project, CommonColumn.Location, CommonColumn.Owner];

export const ColumnsForFlowSubspaces: Record<FlowSubspaceName, ColumnType[]> = {
  'overview': [],
  'connections': [CommonColumn.Select, CommonColumn.ConnectsTo, CommonColumn.ActionsMenu, CommonColumn.ConnectionType, CommonColumn.Authentication, CommonColumn.Username, FlowConnectionInputStepsColumn, FlowConnectionOutputStepsColumn, FlowConnectionErrorColumn],
  'scheduledTasks': [CommonColumn.Select, CommonColumn.FlowScheduleType, CommonColumn.Schedule, CommonColumn.ActionsMenu, CommonColumn.OutputSteps, CommonColumn.FlowParameterWithOverride, CommonColumn.RunFlowTasksStatus, CommonColumn.LastRunAtFlows, CommonColumn.RunNextAtFlows],
  'runHistory': [CommonColumn.OutputStep, CommonColumn.RunType, CommonColumn.FlowParameterRunWithOverride, CommonColumn.RunStartedAt, CommonColumn.RunEndedAt, CommonColumn.RunDuration, CommonColumn.RunStatus, CommonColumn.RunRowsProduced, CommonColumn.RunErrors],
  'subscriptions': [CommonColumn.Select, CommonColumn.UsernameForSubscription, CommonColumn.ActionsMenu, CommonColumn.SubscribedTo, CommonColumn.Subject, CommonColumn.Schedule, CommonColumn.SubscribedBy, CommonColumn.LastUpdate, CommonColumn.RunNextAt],
  'lineage': [],
};

export const ColumnsForPublishedConnectionSubspaces: Record<PublishedConnectionSubspaceName, ColumnType[]> = {
  'tables': [CommonColumn.Select, CommonColumn.ContentType, CommonColumn.PublishedConnectionTableName, CommonColumn.PublishedConnectionPolicies, CommonColumn.PublishedConnectionExtractStatus, CommonColumn.LastRefreshedAt],
  'scheduledTasks': [],
  'lineage': [],
};

export const ColumnsForPublishedConnectionTableSubspaces: Record<PublishedConnectionTableSubspaceName, ColumnType[]> = {
  'overview': [CommonColumn.Select, CommonColumn.DataType, CommonColumn.Name],
  'scheduledTasks': [],
  'lineage': [],
};

export const ColumnsForScheduleSubspaces: Record<ScheduleSubspaceName, ColumnType[]> = {
  'details': [],
  'flows': [CommonColumn.Select, CommonColumn.FlowOrLinkedTask, CommonColumn.ActionsMenu, CommonColumn.OutputStepsWithLinkedTasks, CommonColumn.Priority, CommonColumn.RunFlowTasksStatus, CommonColumn.SiteName, CommonColumn.LastRunAtFlows, CommonColumn.RunNextAtFlows, CommonColumn.RunErrors],
  'extractRefreshes': [CommonColumn.Select, CommonColumn.ExtractTaskName, CommonColumn.ActionsMenu, CommonColumn.TaskType, CommonColumn.Priority, CommonColumn.SiteName, CommonColumn.LastRunAt, CommonColumn.RunNextAt],
  'subscriptions': [CommonColumn.Select, CommonColumn.UsernameForSubscription, CommonColumn.ActionsMenu, CommonColumn.SubscribedTo, CommonColumn.Subject, CommonColumn.SiteName, CommonColumn.SubscribedBy, CommonColumn.LastUpdate, CommonColumn.RunNextAt],
};

export const ColumnsForTaskSubspaces: Record<SiteTasksSubspaceName, ColumnType[]> = {
  'extractRefreshes': [CommonColumn.Select, CommonColumn.TargetType, CommonColumn.ExtractTaskName, CommonColumn.ActionsMenu, CommonColumn.TaskType, CommonColumn.Schedule, CommonColumn.Priority, CommonColumn.LastRunAtExtractRefreshes, CommonColumn.RunNextAt],
  'runFlowTasks': [CommonColumn.Select, CommonColumn.FlowName, CommonColumn.ActionsMenu, CommonColumn.OutputSteps, CommonColumn.Schedule, CommonColumn.Priority, CommonColumn.RunFlowTasksStatus, CommonColumn.LastRunAtFlows, CommonColumn.RunNextAtFlows, CommonColumn.RunErrors],
  'subscriptions': [CommonColumn.Select, CommonColumn.UsernameForSubscription, CommonColumn.ActionsMenu, CommonColumn.SubscribedTo, CommonColumn.Subject, CommonColumn.Schedule, CommonColumn.SubscribedBy, CommonColumn.LastUpdate, CommonColumn.RunNextAt],
  'alerts': [CommonColumn.Select, CommonColumn.DataAlertSubject, CommonColumn.ActionsMenu, CommonColumn.DataAlertView, CommonColumn.Owner, CommonColumn.DataAlertRecepient, CommonColumn.DataAlertVisibleTo, CommonColumn.DataAlertFrequency, CommonColumn.DataAlertLastCheck, CommonColumn.DataAlertLast],
  'dataAcceleration': [CommonColumn.Select, CommonColumn.AccelerationTaskName, CommonColumn.ActionsMenu, CommonColumn.Workbook, CommonColumn.Project, CommonColumn.Location, CommonColumn.Owner, CommonColumn.LastMonthViewCount, CommonColumn.avgLoadTimeWithMaterialization, CommonColumn.avgLoadTimeWithoutMaterialization, CommonColumn.DataAccelerationStatus, CommonColumn.LastMonthJobCount, CommonColumn.lastMonthJobAvgTimeInSec, CommonColumn.EstimatedDiskUsageKb],
  'linkedTasks': [CommonColumn.Select, CommonColumn.LinkedTaskWithCount, CommonColumn.ActionsMenu, CommonColumn.OutputSteps, CommonColumn.Schedule, CommonColumn.Priority, CommonColumn.RunFlowTasksStatus, CommonColumn.LastRunAtFlows, CommonColumn.RunNextAtFlows, CommonColumn.RunErrors],
};

export const ColumnsForServerTaskSubspaces: Record<ServerTasksSubspaceName, ColumnType[]> = {
  'extractRefreshes': [CommonColumn.Select, CommonColumn.TargetType, CommonColumn.ExtractTaskName, CommonColumn.ActionsMenu, CommonColumn.TaskType, CommonColumn.Schedule, CommonColumn.Priority, CommonColumn.SiteName, CommonColumn.LastRunAtExtractRefreshes, CommonColumn.RunNextAt],
  'runFlowTasks': [CommonColumn.Select, CommonColumn.FlowName, CommonColumn.ActionsMenu, CommonColumn.OutputSteps, CommonColumn.Schedule, CommonColumn.Priority, CommonColumn.RunFlowTasksStatus, CommonColumn.SiteName, CommonColumn.LastRunAtFlows, CommonColumn.RunNextAtFlows, CommonColumn.RunErrors],
  'subscriptions': [CommonColumn.Select, CommonColumn.UsernameForSubscription, CommonColumn.ActionsMenu, CommonColumn.SubscribedTo, CommonColumn.Subject, CommonColumn.Schedule, CommonColumn.SiteName, CommonColumn.SubscribedBy, CommonColumn.LastUpdate, CommonColumn.RunNextAt],
  'dataAcceleration': [CommonColumn.Select, CommonColumn.AccelerationTaskName, CommonColumn.ActionsMenu, CommonColumn.Workbook, CommonColumn.Project, CommonColumn.Location, CommonColumn.Owner, CommonColumn.SiteName, CommonColumn.LastMonthViewCount, CommonColumn.avgLoadTimeWithMaterialization, CommonColumn.avgLoadTimeWithoutMaterialization, CommonColumn.DataAccelerationStatus, CommonColumn.LastMonthJobCount, CommonColumn.lastMonthJobAvgTimeInSec, CommonColumn.EstimatedDiskUsageKb],
  'linkedTasks': [CommonColumn.Select, CommonColumn.LinkedTaskWithCount, CommonColumn.ActionsMenu, CommonColumn.OutputSteps, CommonColumn.Schedule, CommonColumn.Priority, CommonColumn.RunFlowTasksStatus, CommonColumn.LastRunAtFlows, CommonColumn.RunNextAtFlows, CommonColumn.RunErrors],
};

export const ColumnsForSharedWithMeSpace: ColumnType[] = [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.NameWithAccessStatus, ...FeatureFlags.sharedWithMeForCustomViewEnabled ? [CommonColumn.SharedCustomizedView] : [], CommonColumn.ActionsMenu, CommonColumn.SharedBy, CommonColumn.SharedAt, CommonColumn.Owner];

export class ListViewGridContentGenerator {

  private columnGenerator: ListViewGridColumnGenerator;

  private params: ListViewGridContentGeneratorParams;
  private rows: DataGrid.Row[] = [];

  public cols: DataGrid.Column[] = [];
  public rowStore = (): DataGrid.RowStore => {
    return {
      getItems: (start: number, end: number, rowHandler: (rows: DataGrid.Row[]) => void) => {

        rowHandler(this.rows);

        // requestSpaceContentsMissingSlice fetches content from start to end, including start and excluding end
        // rowStore is defined as "the last row that will be rendered per the current viewport" so it is inclusive.
        // We need to add one to end to make sure we fetch all of the data.
        this.params.requestSpaceContentsMissingSlice(start, end + 1);
      }
    };
  }

  // Simply match the grid selection to our array of selected items
  public selection = (): GridSelection => {
    return {
      isSelected: (cell: DataGrid.Cell): boolean => {
        if (cell.rowIdx > -1 && cell.rowIdx < this.params.items.length) {
          const item = this.params.items[cell.rowIdx];
          return !!this.params.selectedItems[getKeyForExplorableItem(item)];
        }
      }
    };
  }

  public constructor(params: ListViewGridContentGeneratorParams) {
    this.params = params;
    this.initializeRowsAndColumns();
  }

  public generateContent(params: ListViewGridContentGeneratorParams): void {

    // re initialize columns if one of the below properties changed
    const reInitializeColumns: boolean =
      !_.isEmpty(
        _.omit(
          _.pick(this.params, 'contentFilter', 'spaceInfo', 'inServerArea', 'ignoreServerExtractPriority', 'isGuestEnabled', 'currentSort'),
          function(value: any, key: any) { return (params as any)[key] === value; }
        )
      );

    this.params = params;

    if (reInitializeColumns) {
      this.initializeRowsAndColumns();
    } else {
      // here we don't want to regenerate columns but we do need to reset the items and update
      // column generator with updated props
      this.setRowDataFromItems();
      const { items, requestSpaceContentsMissingSlice: requestSpaceContentsMissingSlice, ignoreExtractTaskPriority, ...columnGeneratorParams } = this.params;
      this.columnGenerator.updateParams({
        ...columnGeneratorParams,
        showingMixedContent: this.params.contentFilter.length > 1,
        displayingItems: items.length > 0
      });
    }
  }

  private setRowDataFromItems() {
    // Row data gets reset when props are updated
    this.rows = new Array(this.params.items.length);
    this.params.items.forEach((item: IContentInfo, index: number) => {
      this.rows[index] = {
        key: item.id,
        data: { ...item, rowIndex: index }  // this has to be rowIndex instead of index so it doesn't override the 'index' in data TFSID944970
      };
    });
  }

  private initializeRowsAndColumns(): void {
    this.setRowDataFromItems();

    /*
     * Guidance toward future refactoring (DRBR, 2019-03-18):
     *
     * My goal is to easily add/define columns for a new content type / subplace. I want those columns
     * to be defined in the module that my team owns (the unique columns, at least). I also want a clear
     * place to list the columns for explorer grids displaying my content type across all the various
     * pages where that grid might appear (preferably, my module would own that listing as well).
     * It seems natural to have the column list be part of the ContentDefinition object for each
     * content type. If the ContentGenerator can reference the column listing from there,
     * the ContentDefinition could easily also point to a list defined in a different module.
     *
     * An idea to make this happen is to pivot this code so that each column list lives in a
     * `listViewColumnTypes(spaceInfo) function on its content type's definition object (in
     * ContentDefinitions.ts). The ContentGenerator would fetch the content definition for whichever
     * content type we're currently viewing via some helper code in SpaceInfo.ts (there would still
     * need to be a special case for mixed content), then pass the space info into the
     * `listViewColumnTypes` function, which would return the correct columns.
     *
     * In addition, columns can now be defined as standalone objects or factory functions, for cases
     * where their implementations are sufficiently unique enough to warrant not living in the
     * ColumnGenerator file itself. We can pull the column definitions out from the giant switch
     * statement, as appropriate, by changing each one into a function that takes a subset of the
     * ColumnGenerator params, or just a static column object. Then, as long as their params are a
     * subset of the ones provided by the ColumnGenerator, special columns can be defined in any module.
     */

    // Filtering on one specific type, simply returning that list of columns for that contenttype
    // Otherwise return the mixed content type columns
    // This assumes the content types are unique in contentFilter
    let columnTypes: ColumnType[] = [];
    const showingMixedContent = this.params.contentFilter.length > 1;

    if (isWorkbookSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForWorkbookSubspaces[this.params.spaceInfo.subspaceName as WorkbookSubspaceName];
    } else if (isDatasourceSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForDatasourceSubspaces[this.params.spaceInfo.subspaceName as DatasourceSubspaceName];
    } else if (isRecentsSpace(this.params.spaceInfo)) {
      if (FeatureFlags.metricsSpaceEnabled) {
        columnTypes = ColumnsForRecentsMixedSpace;
      } else {
        columnTypes = ColumnsForRecentsViewsSpace;
      }
    } else if ((isSingleCollectionSpace(this.params.spaceInfo) ||
      (isFavoritesSpace(this.params.spaceInfo) && FeatureFlags.useCollectionsFavorites)) && showingMixedContent) {
      if (FeatureFlags.metricsSpaceEnabled) {
        columnTypes = ColumnsForMixedContentTypesWithLocation;
      } else {
        columnTypes = ColumnsForMixedContentTypesWithoutMetricsWithLocation;
      }
      if (!FeatureFlags.useCollectionsFavorites && isSingleCollectionSpace(this.params.spaceInfo)) {
        columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.Favorite);
      }
    } else if (isFlowSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForFlowSubspaces[this.params.spaceInfo.subspaceName as FlowSubspaceName];
    } else if (isPublishedConnectionSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForPublishedConnectionSubspaces[this.params.spaceInfo.subspaceName as PublishedConnectionSubspaceName];
    } else if (isPublishedConnectionTableSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForPublishedConnectionTableSubspaces[this.params.spaceInfo.subspaceName as PublishedConnectionTableSubspaceName];
    } else if (isDataRoleSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForDataRoleSubspaces[this.params.spaceInfo.subspaceName as DataRoleSubspaceName];
    } else if (isScheduleSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForScheduleSubspaces[this.params.spaceInfo.subspaceName as ScheduleSubspaceName];
      if (!this.params.inServerArea) {
        columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.SiteName);
      }
    } else if (isSiteTasksSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForTaskSubspaces[this.params.spaceInfo.subspaceName as SiteTasksSubspaceName];
    } else if (isUnifiedSearchSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForCRASSSearch;
    } else if (isServerTasksSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForServerTaskSubspaces[this.params.spaceInfo.subspaceName as ServerTasksSubspaceName];
    } else if (isGroupSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForGroupSubspaces[this.params.spaceInfo.subspaceName as GroupSubspaceName];
    } else if (isSharedWithMeSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForSharedWithMeSpace;
    } else if (showingMixedContent) {
      if (_.contains(this.params.contentFilter, 'metric')) {
        columnTypes = ColumnsForMixedContentTypes;
      } else {
        columnTypes = ColumnsForMixedContentTypesWithoutMetrics;
      }
    } else if (this.params.contentFilter.length === 1) {
      columnTypes = this.getSpecificContentTypeColumns(this.params.contentFilter[0]);
    }

    if (isSiteUserContentSubspace(this.params.spaceInfo) || isMyCollectionsSubspace(this.params.spaceInfo)) {
      columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.Owner);
    }

    // remove the extract task priority column if we are told to ignore it
    if (this.params.ignoreExtractTaskPriority) {
      columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.Priority);
    }

    if (!FeatureFlags.personalSpaceEnabled) {
      columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.Location);
    }

    if (FeatureFlags.personalSpaceEnabled) {
      columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.Project);
    }

    if (FeatureFlags.personalSpaceEnabled && isPersonalSpace(this.params.spaceInfo)) {
      columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.Location);
    }

    if (isSingleCollectionSpace(this.params.spaceInfo)) {
      columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.Location);
    }

    if (isSiteUsersSpace(this.params.spaceInfo) || isGroupUsersSubspace(this.params.spaceInfo)) {
      if (!ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory()) {
        columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.SiteUserDomainName);
      }

      if (!SiteAuthenticationSettingsService.isEnabled()) {
        columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.SiteUserAuthentication);
      }
    }

    if (!areFlowParametersEnabled()) {
      columnTypes = _.filter(columnTypes, (columnType) => {
        return columnType !== CommonColumn.FlowParameterRunWithOverride && columnType !== CommonColumn.FlowParameterWithOverride;
      });
    }

    const { items, requestSpaceContentsMissingSlice: requestSpaceContentsMissingSlice, ignoreExtractTaskPriority, ...columnGeneratorParams } = this.params;
    this.columnGenerator = new ListViewGridColumnGenerator({
      ...columnGeneratorParams,
      showingMixedContent: showingMixedContent,
      displayingItems: items.length > 0
    });

    this.cols = _.compact(columnTypes.map((columnType) => this.columnGenerator.getColumn(columnType)));

    // Replace select column's cell formatter with dc3's version. This is done only until cell formatters are implemented in VPC.
    // Story 1200630: Replace the DC3 cell formatters in ListViewGridContentGenerator when similar cell formatters are added to VPC
    if (FeatureFlags.crassforDbsAndTablesEnabled) {
      if (isDatabasesWithCrassSearchSpace(this.params.spaceInfo) || isCatalogDatabaseWithCrassSpace(this.params.spaceInfo)) {
        replaceColumnCellFormattersForDatabasesAndTables(databasesWithCrassCellFormatters, this.cols);
      } else if (isTablesWithCrassSearchSpace(this.params.spaceInfo) || isCatalogTableWithCrassSpace(this.params.spaceInfo)) {
        replaceColumnCellFormattersForDatabasesAndTables(tablesWithCrassCellFormatters, this.cols);
      }
    }

    if (!FeatureFlags.collectionsGAEnabled && isSingleCollectionSpace(this.params.spaceInfo)) {
      this.cols = this.cols.map((col) => ({ ...col, sortable: false }));
    }

    // Once Story 1200630: Replace the DC3 cell formatters in ListViewGridContentGenerator when similar cell formatters are added to VPC is done
    // will need to modify this telemtry code as well
    if (isDatabasesSearchSpace(this.params.spaceInfo) || isTablesWithSearchSpace(this.params.spaceInfo) || isDatabasesWithCrassSearchSpace(this.params.spaceInfo) || isTablesWithCrassSearchSpace(this.params.spaceInfo)) {
      const nameColumn = this.cols.find(col => col.dataKey === CommonColumn.Name);
      if (nameColumn) {
        const copyiedNameFormatter = nameColumn.cellFormatter;
        nameColumn.cellFormatter = (prop) =>
        (<SearchTermDataContext.Consumer>
          {searchTerm =>
            <cellClickingTelemetryContext.Provider value={((childFunction: (handler: () => void) => React.ReactElement) => childFunction(() => {
              searchTelemetry.sendSearchResultClickedEvent({
                type: TelemetryEvents.SearchResultClicked,
                eventSource: 'search-space',
                resultType: prop.row.data.contentType,
                seeAll: false,
                index: prop.row.data.rowIndex + 1,
                total: this.rows.length,
                searchTerm: searchTerm,
                contentId: prop.row.data.id,
              });
            }))}>
              {copyiedNameFormatter(prop)}
            </cellClickingTelemetryContext.Provider>
          }</SearchTermDataContext.Consumer>);
      }
    }
  }

  private getSpecificContentTypeColumns(contentType: ExplorableItemType): ColumnType[] {

    let columnTypes = ColumnsForContentType[contentType];

    if (!areLinkedTasksEnabled()) {
      columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.ScheduleLinkedTask);
    }
    if (!FeatureFlags.modifiedByUserEnabled) {
      columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.ModifiedBy);
    }

    // Don't show the project column if we are in a project space
    if (isProjectSpace(this.params.spaceInfo) && FeatureFlags.personalSpaceEnabled) {
      columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.Location);
    }

    if (isProjectSpace(this.params.spaceInfo) && !FeatureFlags.personalSpaceEnabled) {
      columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.Project);
    }

    if (isSiteUserSpace(this.params.spaceInfo)) {
      // Data alerts subspace isn't list of alerts 'owned' by the user.
      // It is list of alerts being received by the user, so the owner column still makes sense.
      if (!isSiteUserDataAlertsSubSpace(this.params.spaceInfo)) {
        columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.Owner);
      }

      if (!FeatureFlags.useCollectionsFavorites && isSiteUserCollectionsSubspace(this.params.spaceInfo)) {
        columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.Favorite);
      }
    }

    if (isAllServerUsersSpace(this.params.spaceInfo)) {
      if (!ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory()) {
        columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.SiteUserDomainName);
      }
    }

    if (isAllSitesSpace(this.params.spaceInfo)) {
      const sessionManagementService = getSessionManagementServiceInstance();
      if (!sessionManagementService.isExtractEncryptionEnabledOmniSwitch()) {
        columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.SiteExtractEncryption);
      }

      if (!this.params.isGuestEnabled) {
        columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.SiteGuestAccessEnabled);
      }
    }

    if (isBackgroundJobsSpace(this.params.spaceInfo)) {
      if (!FeatureFlags.jobManagement194UIEnabled) {
        columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.AverageQueueTime && columnType !== CommonColumn.AverageRunTime);
      }
    }

    if (contentType !== 'datasource' && contentType !== 'flow' && contentType !== 'publishedConnection') {
      columnTypes = _.filter(columnTypes, (columnType) => (columnType) !== CommonColumn.DataQualityIndicator);
    }

    if (!FeatureFlags.licenseUponLoginEnabled) {
      columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.GroupLicenseOnSignIn);
    }

    if (!FeatureFlags.useCollectionsFavorites && isCollectionsSpace(this.params.spaceInfo)) {
      columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.Favorite);
    }

    // CRASS doesn't send the Connection Details type needed to display. Track with TFS 1239325
    if (isSingleCollectionSpace(this.params.spaceInfo)) {
      columnTypes = _.filter(columnTypes, (columnType) => (columnType !== CommonColumn.ConnectsTo && columnType !== CommonColumn.DataQualityIndicator));
    }

    if (isCatalogSpace(this.params.spaceInfo) || isCatalogSearchSpace(this.params.spaceInfo)) {
      const databaseAndTablesActionsColIndex = 5;
      columnTypes.splice(databaseAndTablesActionsColIndex, 0, CommonColumn.ActionsMenuForCatalog);
    }

    return columnTypes;
  }
}
